import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Alert, Grid, Snackbar, useMediaQuery } from "@mui/material";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import MembersTabs from "./MembersTabs";
import AddMemberModal from "../../components/AddMemberModal/AddMemberModal";
import styles from "./MembersPageStyles";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Member {
  codename: string;
  gender: string;
  dob: string;
  member_status: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface MembersPageProps {}

const MembersPage: React.FC<MembersPageProps> = () => {
  const isMobile = useMediaQuery("(max-width:1100px)");
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const { MembersContainer } = styles;
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<Member[]>(
        `${process.env.REACT_APP_BACKEND_URL}/members`
      );
      setMembers(response.data);
    } catch (error) {
      console.error("Error fetching participants:", error);
      handleShowSnackbar("Error fetching participants", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleOpenAddMemberModal = () => {
    setIsAddMemberModalOpen(true);
  };

  const handleShowSnackbar = useCallback(
    (message: string, severity: "success" | "error") => {
      console.log("Showing snackbar:", message, severity); // Debug log
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleCloseAddMemberModal = useCallback(() => {
    setIsAddMemberModalOpen(false);
  }, []);

  const handleMemberAdded = useCallback(() => {
    fetchMembers();
  }, [fetchMembers]);

  if (loading) return <LoadingSpinner />;

  return (
    <Grid container sx={MembersContainer}>
      <Grid item xs={2.5}>
        {!isMobile && <Sidebar />}
      </Grid>
      <Grid item xs={9}>
        <Header />
        <MembersTabs
          onOpenAddMemberModal={handleOpenAddMemberModal}
          members={members}
          refreshMembers={fetchMembers}
          onShowSnackbar={handleShowSnackbar}
        />
      </Grid>

      {/* AddMemberModal - Moved outside the grid system to prevent modal positioning issues */}
      <AddMemberModal
        open={isAddMemberModalOpen}
        onClose={handleCloseAddMemberModal}
        onMemberAdded={handleMemberAdded}
        onShowSnackbar={handleShowSnackbar}
      />

      {/* Global Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ maxWidth: "80%" }} // Added to prevent extremely wide snackbars
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            "& .MuiAlert-message": {
              maxWidth: "100%",
              wordBreak: "break-word",
            },
          }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default MembersPage;
